import { print } from 'graphql';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import {
  findByIdCourseCategoryQuery,
  findManyCourseCategoryQuery,
  updateOrderCourseCategoryMutation,
} from '~/shared/graphql-queries';
import withZeroLessonCourseCategoryFragment, {
  basicCourseCategoryFragment,
} from '~/shared/graphql-queries/course-categories/course-category-fragment';
import createOneCourseCategoryMutation from '~/shared/graphql-queries/course-categories/create-one-course-category-mutation';
import findByCourseAndIndexCourseCategoryQuery from '~/shared/graphql-queries/course-categories/find-by-course-and-index-course-category-query';
import findManyLightCourseCategoryQuery from '~/shared/graphql-queries/course-categories/find-many-light-course-category-query';
import findOneCourseCategoryQuery from '~/shared/graphql-queries/course-categories/find-one-course-category-query';
import removeByIdCourseCategoryMutation from '~/shared/graphql-queries/course-categories/remove-by-id-course-category-mutation';
import updateByIdCourseCategoryMutation from '~/shared/graphql-queries/course-categories/update-by-id-course-category-mutation';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';

type PaginationCourseCategoryResponse = Readonly<{
  data?: {
    paginationCourseCategoryQuery?: {
      items: Record<string, unknown>[];
      pageInfo: PageInfoType;
    };
  };
}>;

type PaginationCourseCategoryResult = Readonly<{
  items: Record<string, unknown>[];
  pageInfo: PageInfoType;
}>;

type FindByIdCourseCategoryArgs = Readonly<{
  _id?: string;
}>;

type FindOneCourseCategoryArgs = Readonly<{
  filter?: Record<string, unknown>;
}>;

type PaginationCourseCategoryArgs = Readonly<{
  filter?: Record<string, unknown>;
  nextPage?: number;
  sort?: string;
  perPage?: number;
  withZeroLesson?: boolean;
}>;

type UpdateByIdCourseCategoryArgs = Readonly<{
  _id?: string;
  record?: Record<string, unknown>;
}>;

type UpdateOrderCourseCategoryArgs = Readonly<{
  courseCategories?: unknown[];
}>;

type CreateOneCourseCategoryArgs = Readonly<{
  course?: string;
}>;

type FindManyCourseCategoryArgs = Readonly<{
  limit?: number;
  filter?: Record<string, unknown>;
}>;

type FindByCourseAndIndexCourseCategoryArgs = Readonly<{
  courseId?: string;
  index?: number;
}>;

type RemoveByIdCourseCategoryArgs = Readonly<{
  _id?: string;
}>;

const transformCourseCategoryResponse =
  (type: 'paginationCourseCategoryQuery') =>
  (response: PaginationCourseCategoryResponse): PaginationCourseCategoryResult => {
    return response.data?.[type] ?? { items: [], pageInfo: {} as PageInfoType };
  };

export const courseCategoriesTagTypes = ['course-categories'] as const;

export const getCourseCategoriesQuery = (builder: BuilderType) => {
  return {
    findByIdCourseCategory: builder.query<unknown, FindByIdCourseCategoryArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findByIdCourseCategoryQuery),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),

    findOneCourseCategory: builder.query<unknown, FindOneCourseCategoryArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findOneCourseCategoryQuery),
          variables: {
            filter: args?.filter,
          },
        }),
      }),
    }),

    paginationCourseCategory: builder.query<PaginationCourseCategoryResult, PaginationCourseCategoryArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'courseCategory',
              sortType: 'SortEnum',
              fragmentName: args?.withZeroLesson ? 'CourseCategoryFragment' : 'BasicCourseCategoryFragment',
              fragment: args?.withZeroLesson ? withZeroLessonCourseCategoryFragment : basicCourseCategoryFragment,
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? SORT_TYPE._ID_ASC,
            perPage: args?.perPage
              ? args.perPage
              : args?.nextPage === 1
                ? LIMIT_FOR_FETCHING_ADMIN_ITEMS + 1
                : LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['course-categories'],
      transformResponse: transformCourseCategoryResponse('paginationCourseCategoryQuery'),
    }),

    updateByIdCourseCategory: builder.mutation<unknown, UpdateByIdCourseCategoryArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateByIdCourseCategoryMutation),
          variables: {
            _id: args?._id,
            record: args?.record,
          },
        }),
      }),
    }),

    updateOrderCourseCategory: builder.mutation<unknown, UpdateOrderCourseCategoryArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateOrderCourseCategoryMutation),
          variables: {
            courseCategories: args?.courseCategories,
          },
        }),
      }),
    }),

    createOneCourseCategory: builder.mutation<unknown, CreateOneCourseCategoryArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createOneCourseCategoryMutation),
          variables: {
            course: args?.course,
          },
        }),
      }),
    }),

    findManyCourseCategory: builder.query<unknown, FindManyCourseCategoryArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findManyCourseCategoryQuery),
          variables: {
            limit: args?.limit,
            filter: args?.filter,
          },
        }),
      }),
    }),

    findManyLightCourseCategory: builder.query<unknown, FindManyCourseCategoryArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findManyLightCourseCategoryQuery),
          variables: {
            limit: args?.limit,
            filter: args?.filter,
          },
        }),
      }),
    }),

    findByCourseAndIndexCourseCategory: builder.query<unknown, FindByCourseAndIndexCourseCategoryArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findByCourseAndIndexCourseCategoryQuery),
          variables: {
            courseId: args?.courseId,
            index: args?.index,
          },
        }),
      }),
    }),

    removeByIdCourseCategory: builder.mutation<unknown, RemoveByIdCourseCategoryArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: removeByIdCourseCategoryMutation.loc?.source?.body,
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),
  };
};
